// pages/RawBillList.js - Raw Bill List Page

import { useState, useEffect } from "react";
import { database } from "../firebase/firebase";
import { ref, onValue, remove, get } from "firebase/database";
import { useNavigate } from "react-router-dom";
import "../styles/main.css";
import SimpleBillPrintService from '../components/SimpleBillPrintService';

export default function RawBillList() {
  const navigate = useNavigate();
  const printService = new SimpleBillPrintService();
  
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [bills, setBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [companyDetails, setCompanyDetails] = useState(null);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState({
    totalBills: 0,
    totalRevenue: 0,
    totalDiscounts: 0
  });

  // Load company details and settings
  useEffect(() => {
    // Load company details
    const companyRef = ref(database, "company");
    onValue(companyRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCompanyDetails(data);
      }
    });

    // Load settings
    const settingsRef = ref(database, "settings");
    onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setSettings(data);
      }
    });
  }, []);

  // Load all financial years
  useEffect(() => {
    const financialYearsRef = ref(database, "financialYears");
    onValue(financialYearsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const years = Object.keys(data).sort().reverse();
        setFinancialYears(years);
        if (years.length > 0 && !selectedYear) {
          setSelectedYear(years[0]);
        }
      }
      setLoading(false);
    });
  }, []);

  // Load bills for selected financial year
  useEffect(() => {
    if (!selectedYear) return;

    const billsRef = ref(database, `financialYears/${selectedYear}/rawBills`);
    onValue(billsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const billList = Object.entries(data).map(([id, bill]) => ({
          id,
          ...bill
        })).sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
        setBills(billList);
        setFilteredBills(billList);
        
        // Calculate stats
        const totalRevenue = billList.reduce((sum, bill) => sum + (bill.grandTotal || 0), 0);
        const totalDiscounts = billList.reduce((sum, bill) => sum + (bill.totalDiscount || 0), 0);
        
        setStats({
          totalBills: billList.length,
          totalRevenue,
          totalDiscounts
        });
      } else {
        setBills([]);
        setFilteredBills([]);
        setStats({
          totalBills: 0,
          totalRevenue: 0,
          totalDiscounts: 0
        });
      }
    });
  }, [selectedYear]);

  // Search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredBills(bills);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = bills.filter(bill => {
      return (
        bill.billNo?.toLowerCase().includes(term) ||
        bill.customerName?.toLowerCase().includes(term) ||
        bill.customerMobile?.includes(term) ||
        bill.notes?.toLowerCase().includes(term) ||
        bill.items?.some(item => 
          item.name?.toLowerCase().includes(term)
        )
      );
    });
    
    setFilteredBills(filtered);
  }, [searchTerm, bills]);

  const handleDelete = async (billId) => {
    if (!selectedYear) return;
    
    if (window.confirm("Are you sure you want to delete this bill?")) {
      try {
        const billRef = ref(database, `financialYears/${selectedYear}/rawBills/${billId}`);
        await remove(billRef);
        alert("Bill deleted successfully");
      } catch (error) {
        console.error("Error deleting bill:", error);
        alert("Failed to delete bill");
      }
    }
  };

  const handleEdit = (billId) => {
    navigate(`/raw-bill/edit/${billId}`);
  };

  const handleView = (billId) => {
    navigate(`/raw-bill/view/${billId}`);
  };

  const handlePrint = async (bill) => {
    try {
      // Fetch full bill details if needed
      const billRef = ref(database, `financialYears/${selectedYear}/rawBills/${bill.id}`);
      const snapshot = await get(billRef);
      
      if (snapshot.exists()) {
        const fullBill = snapshot.val();
        
        const totals = {
          subtotal: fullBill.subtotal || 0,
          totalItemDiscount: fullBill.totalItemDiscount || 0,
          globalDiscount: fullBill.globalDiscount || 0,
          totalDiscount: fullBill.totalDiscount || 0,
          totalCharges: fullBill.totalCharges || 0,
          grandTotal: fullBill.grandTotal || 0,
          roundedTotal: fullBill.roundedTotal || 0,
          roundOff: fullBill.roundOff || 0
        };
        
        const printData = {
          settings: settings || {},
          company: companyDetails || {
            name: fullBill.companyDetails?.name || "",
            address: fullBill.companyDetails?.address || "",
            phone: fullBill.companyDetails?.phone || ""
          },
          customer: {
            id: fullBill.customerId,
            name: fullBill.customerName,
            mobile: fullBill.customerMobile,
            address: fullBill.customerAddress
          },
          items: fullBill.items || [],
          totals: totals,
          billNo: fullBill.billNo,
          billDate: fullBill.date,
          notes: fullBill.notes || "",
          terms: fullBill.terms || fullBill.companyDetails?.terms || "Goods once sold will not be taken back.\nInterest @ 18% P.A. will be charged if it is not paid on presentation."
        };

        printService.print(printData);
      }
    } catch (error) {
      console.error("Error printing bill:", error);
      alert("Failed to print bill");
    }
  };

  const handlePreview = async (bill) => {
    try {
      // Fetch full bill details if needed
      const billRef = ref(database, `financialYears/${selectedYear}/rawBills/${bill.id}`);
      const snapshot = await get(billRef);
      
      if (snapshot.exists()) {
        const fullBill = snapshot.val();
        
        const totals = {
          subtotal: fullBill.subtotal || 0,
          totalItemDiscount: fullBill.totalItemDiscount || 0,
          globalDiscount: fullBill.globalDiscount || 0,
          totalDiscount: fullBill.totalDiscount || 0,
          totalCharges: fullBill.totalCharges || 0,
          grandTotal: fullBill.grandTotal || 0,
          roundedTotal: fullBill.roundedTotal || 0,
          roundOff: fullBill.roundOff || 0
        };
        
        const printData = {
          settings: settings || {},
          company: companyDetails || {
            name: fullBill.companyDetails?.name || "",
            address: fullBill.companyDetails?.address || "",
            phone: fullBill.companyDetails?.phone || ""
          },
          customer: {
            id: fullBill.customerId,
            name: fullBill.customerName,
            mobile: fullBill.customerMobile,
            address: fullBill.customerAddress
          },
          items: fullBill.items || [],
          totals: totals,
          billNo: fullBill.billNo,
          billDate: fullBill.date,
          notes: fullBill.notes || "",
          terms: fullBill.terms || fullBill.companyDetails?.terms || "Goods once sold will not be taken back.\nInterest @ 18% P.A. will be charged if it is not paid on presentation."
        };

        printService.preview(printData);
      }
    } catch (error) {
      console.error("Error previewing bill:", error);
      alert("Failed to preview bill");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading bills...</p>
      </div>
    );
  }

  return (
    <div className="raw-bill-list-app">
      <div className="page-header">
        <h2>Raw Bills</h2>
        <button 
          className="create-btn"
          onClick={() => navigate("/raw-bill/new")}
        >
          + New Bill
        </button>
      </div>

      {/* Financial Year Selector */}
      <div className="financial-year-selector">
        <label>Financial Year:</label>
        <select 
          value={selectedYear} 
          onChange={(e) => setSelectedYear(e.target.value)}
          className="year-select"
        >
          {financialYears.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by bill no., customer name, mobile, items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search" onClick={clearSearch}>
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="search-results-count">
            Found {filteredBills.length} {filteredBills.length === 1 ? 'bill' : 'bills'}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats.totalBills > 0 && (
        <div className="stats-cards">
          <div className="stat-card">
            <span className="stat-label">Total Bills</span>
            <span className="stat-value">{stats.totalBills}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Total Revenue</span>
            <span className="stat-value">{formatCurrency(stats.totalRevenue)}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Total Discounts</span>
            <span className="stat-value">{formatCurrency(stats.totalDiscounts)}</span>
          </div>
        </div>
      )}

      {/* Bills List */}
      <div className="bills-container">
        {filteredBills.length === 0 ? (
          <div className="empty-state">
            {searchTerm ? (
              <>
                <p>No bills found matching "{searchTerm}"</p>
                <button 
                  className="clear-search-btn"
                  onClick={clearSearch}
                >
                  Clear Search
                </button>
              </>
            ) : (
              <>
                <p>No bills found for financial year {selectedYear}</p>
                <button 
                  className="create-first-btn"
                  onClick={() => navigate("/raw-bill/new")}
                >
                  Create First Bill
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="bill-cards">
            {filteredBills.map(bill => (
              <div key={bill.id} className="bill-card">
                <div className="bill-card-header">
                  <span className="bill-no">{bill.billNo}</span>
                  <span className="bill-type">Raw Bill</span>
                </div>
                
                <div className="bill-card-body">
                  <div className="customer-info">
                    <strong>{bill.customerName || "Walk-in Customer"}</strong>
                    {bill.customerMobile && (
                      <span>{bill.customerMobile}</span>
                    )}
                  </div>
                  
                  <div className="bill-details">
                    <span>📅 {formatDate(bill.date)}</span>
                    <span className="amount">💰 {formatCurrency(bill.grandTotal)}</span>
                  </div>
                  
                  {bill.items && (
                    <div className="items-count">
                      {bill.items.length} {bill.items.length === 1 ? 'item' : 'items'}
                    </div>
                  )}

                  {/* Additional Charges & Discounts Preview */}
                  {(bill.additionalCharges?.length > 0 || bill.discounts?.length > 0) && (
                    <div className="extra-info">
                      {bill.additionalCharges?.length > 0 && (
                        <span className="info-tag">
                          +{bill.additionalCharges.length} charges
                        </span>
                      )}
                      {bill.discounts?.length > 0 && (
                        <span className="info-tag discount-tag">
                          -{bill.discounts.length} discounts
                        </span>
                      )}
                    </div>
                  )}

                  {/* Preview of items for search context */}
                  {searchTerm && bill.items && (
                    <div className="item-preview">
                      {bill.items.slice(0, 2).map((item, idx) => (
                        <span key={idx} className="item-tag">
                          {item.name}
                          {item.qty > 1 && ` (${item.qty})`}
                        </span>
                      ))}
                      {bill.items.length > 2 && (
                        <span className="item-tag more">+{bill.items.length - 2} more</span>
                      )}
                    </div>
                  )}

                  {/* Notes Preview */}
                  {bill.notes && (
                    <div className="notes-preview">
                      📝 {bill.notes.length > 60 ? bill.notes.substring(0, 60) + '...' : bill.notes}
                    </div>
                  )}
                </div>
                
                <div className="bill-card-actions">
                  <button 
                    className="view-btn"
                    onClick={() => handleView(bill.id)}
                    title="View Bill"
                  >
                    👁️ View
                  </button>
                  <button 
                    className="preview-btn"
                    onClick={() => handlePreview(bill)}
                    title="Preview Bill"
                  >
                    👁️ Preview
                  </button>
                  <button 
                    className="print-btn"
                    onClick={() => handlePrint(bill)}
                    title="Print Bill"
                  >
                    🖨️ Print
                  </button>
                  <button 
                    className="edit-btn"
                    onClick={() => handleEdit(bill.id)}
                    title="Edit Bill"
                  >
                    ✏️ Edit
                  </button>
                  <button 
                    className="delete-btn"
                    onClick={() => handleDelete(bill.id)}
                    title="Delete Bill"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>

      <style>{`
        .raw-bill-list-app {
          min-height: 100vh;
          background: #f5f5f5;
          padding-bottom: 70px;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 15px 20px;
          background: white;
          border-bottom: 1px solid #eee;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .page-header h2 {
          margin: 0;
          font-size: 20px;
          color: #333;
        }

        .create-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
        }

        .financial-year-selector {
          padding: 15px 20px;
          background: white;
          margin: 10px 20px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 15px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .financial-year-selector label {
          font-weight: 500;
          color: #666;
        }

        .year-select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }

        /* Search Styles */
        .search-container {
          padding: 0 20px;
          margin-bottom: 20px;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          overflow: hidden;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          color: #999;
          font-size: 16px;
        }

        .search-input {
          flex: 1;
          padding: 14px 14px 14px 40px;
          border: 1px solid #ddd;
          border-radius: 12px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
        }

        .search-input:focus {
          border-color: #5a60ea;
        }

        .clear-search {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          color: #999;
          font-size: 16px;
          cursor: pointer;
          padding: 5px;
        }

        .clear-search:hover {
          color: #ff4444;
        }

        .search-results-count {
          margin-top: 8px;
          font-size: 13px;
          color: #666;
          text-align: right;
        }

        .clear-search-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 20px;
          margin-top: 15px;
          cursor: pointer;
        }

        /* Stats Cards */
        .stats-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          padding: 0 20px;
          margin-bottom: 20px;
        }

        .stat-card {
          background: white;
          padding: 15px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          text-align: center;
        }

        .stat-label {
          display: block;
          font-size: 12px;
          color: #666;
          margin-bottom: 5px;
        }

        .stat-value {
          display: block;
          font-size: 18px;
          font-weight: bold;
          color: #5a60ea;
        }

        .bills-container {
          padding: 0 20px;
        }

        .empty-state {
          background: white;
          border-radius: 12px;
          padding: 40px 20px;
          text-align: center;
          color: #666;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .create-first-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 20px;
          margin-top: 15px;
          cursor: pointer;
        }

        .bill-cards {
          display: grid;
          grid-template-columns: 1fr;
          gap: 15px;
        }

        @media (min-width: 768px) {
          .bill-cards {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .bill-cards {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .bill-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          transition: transform 0.2s;
        }

        .bill-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .bill-card-header {
          background: #f8f9fa;
          padding: 12px 15px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .bill-no {
          font-weight: bold;
          color: #5a60ea;
        }

        .bill-type {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 12px;
          background: #e8f0fe;
          color: #5a60ea;
        }

        .bill-card-body {
          padding: 15px;
        }

        .customer-info {
          margin-bottom: 10px;
        }

        .customer-info strong {
          display: block;
          font-size: 16px;
          margin-bottom: 3px;
        }

        .customer-info span {
          font-size: 12px;
          color: #666;
        }

        .bill-details {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: #666;
          margin-bottom: 8px;
        }

        .amount {
          font-weight: bold;
          color: #5a60ea;
        }

        .items-count {
          font-size: 12px;
          color: #888;
          border-top: 1px dashed #eee;
          padding-top: 8px;
        }

        /* Extra Info Styles */
        .extra-info {
          display: flex;
          gap: 5px;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #eee;
        }

        .info-tag {
          background: #f0f0f0;
          color: #2e7d32;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 10px;
        }

        .info-tag.discount-tag {
          color: #d32f2f;
        }

        /* Item Preview Styles */
        .item-preview {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #eee;
        }

        .item-tag {
          background: #f0f0f0;
          color: #666;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 10px;
        }

        .item-tag.more {
          background: #e3f2fd;
          color: #1976d2;
        }

        /* Notes Preview */
        .notes-preview {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #eee;
          font-size: 11px;
          color: #888;
          font-style: italic;
        }

        /* Action Buttons */
        .bill-card-actions {
          display: flex;
          border-top: 1px solid #eee;
        }

        .bill-card-actions button {
          flex: 1;
          padding: 10px;
          border: none;
          background: white;
          cursor: pointer;
          font-size: 12px;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 3px;
        }

        .bill-card-actions button:hover {
          background: #f5f5f5;
        }

        .view-btn {
          color: #5a60ea;
        }

        .preview-btn {
          color: #2196f3;
          border-left: 1px solid #eee;
        }

        .print-btn {
          color: #4caf50;
          border-left: 1px solid #eee;
        }

        .edit-btn {
          color: #ff9800;
          border-left: 1px solid #eee;
          border-right: 1px solid #eee;
        }

        .delete-btn {
          color: #f44336;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: #f5f5f5;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #f3f3f3;
          border-top-color: #5a60ea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 100;
        }

        .bottom-nav div {
          text-align: center;
          cursor: pointer;
          color: #999;
          font-size: 20px;
        }

        .bottom-nav p {
          margin: 2px 0 0;
          font-size: 11px;
        }
      `}</style>
    </div>
  );
}